import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Box, Button, EmptyState, Select, Spinner, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type RunnerLimitQueueItem } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';

/**
 * Runner limits — the queue of raise requests (Figma admin frame `1793:5`).
 * A request is a Runner asking a PERSON to read their record; the row shows
 * the ask beside what they have and what they're using, and "Review" opens
 * the one-screen record pack. Oldest pending first, so nobody waits longest.
 */
const GRID = '1.5fr 0.9fr 1fr 1fr 0.9fr 0.7fr';
const STAGE_LABEL: Record<string, string> = { starting: 'Starting line', trusted: 'Trusted', trusted90: 'Trusted · 90d' };
const KIND_LABEL: Record<string, string> = { deliveries: 'Deliveries', households: 'Households', both: 'Both' };
type Filter = 'pending' | 'approved' | 'declined' | 'all';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function ceiling(n: number | null): string {
  return n === null ? '∞' : String(n);
}

export default function RunnerLimitRequests() {
  const caps = useCapabilities();
  const canView = caps.has('runner:read');
  const canDecide = caps.has('runner:limits:review');
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('pending');
  const [items, setItems] = useState<RunnerLimitQueueItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listRunnerLimitRequests(filter);
      setItems(res.requests);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load limit requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  if (caps.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!canView) return <Alert severity="error">You don’t have permission to view Runner limits.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Runner limits</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Requests for a higher weekly-deliveries or households ceiling. A person reads the record and decides; every decision emails the Runner.
        {counts.pending ? ` ${counts.pending} waiting.` : ''}
      </Typography>
      {!canDecide && (
        <Box sx={{ mb: 2 }}><Alert severity="info">View-only access — you can read records but can’t decide or set limits.</Alert></Box>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select ariaLabel="Filter by status" label="Status" native size="small" sx={{ width: 200 }} value={filter} onChange={(_e, v) => setFilter(v as Filter)}
          options={[{ value: 'pending', label: `Waiting (${counts.pending ?? 0})` }, { value: 'approved', label: `Approved (${counts.approved ?? 0})` }, { value: 'declined', label: `Declined (${counts.declined ?? 0})` }, { value: 'all', label: 'All' }]} />
      </Box>
      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>
      ) : items.length === 0 ? (
        <EmptyState icon="onlineordering" title="Nothing waiting" body={filter === 'pending' ? 'No Runner is asking for a higher limit right now.' : 'No requests match this filter.'} />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {['Runner', 'Asked', 'Asking for', 'Has · using', 'Stage', ''].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{h.toUpperCase()}</Typography>
            ))}
          </Box>
          {items.map((r, idx) => (
            <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.5, alignItems: 'center', borderBottom: idx < items.length - 1 ? '1px solid #eef2f6' : 'none', '&:hover': { backgroundColor: '#fafbfc' } }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{r.runner.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.runner.glpdId ?? '—'}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{fmtDate(r.createdAt)}</Typography>
              <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600 }}>
                {KIND_LABEL[r.kind]}: {[r.requestedDeliveries !== null ? `${r.requestedDeliveries}/wk` : null, r.requestedHouseholds !== null ? `${r.requestedHouseholds} hh` : null].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {ceiling(r.runner.limits.deliveries)}/wk · {r.runner.limits.households} hh
                <br />
                <Typography component="span" variant="caption">using {r.runner.usage.weeklyCapacity}/wk · {r.runner.usage.activeHouseholds} hh</Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Pill label={STAGE_LABEL[r.runner.stage] ?? r.runner.stage} fg="#343F5B" bg="#e8ebf2" />
                {r.status !== 'pending' && <Pill label={r.status} fg={r.status === 'approved' ? '#15803d' : '#64748b'} bg={r.status === 'approved' ? '#dcfce7' : '#f1f5f9'} />}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Button ariaLabel={`Review ${r.runner.name}`} buttonText={r.status === 'pending' ? 'Review' : 'Open'} variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => navigate(`/runner-limits/requests/${r.id}`)} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
